// FastAddyRunner.tsx
// Minimal, embeddable React component of a runner game with hurdles.
// Controls: Space bar or on-screen Jump button (also click/tap canvas).
// No dependencies.

import React, { useEffect, useRef, useState, useCallback } from "react";

interface FastAddyRunnerProps {
  width?: number;
  height?: number;
}

export default function FastAddyRunner({ width = 720, height = 360 }: FastAddyRunnerProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number>(0);

  // Mutable game state kept in refs to avoid re-renders
  const gameRef = useRef({
    speed: 300, // px/s
    speedGain: 25, // px/s^2
    gravity: 2200, // px/s^2
    jumpVel: 900, // px/s
    trackH: Math.floor(height * 0.35),
    lastSpawn: 0,
    hurdles: [] as { x: number; y: number; h: number }[],
    score: 0,
  });

  const playerRef = useRef({
    x: 160,
    y: 0, // set on start
    r: 26,
    vy: 0,
    onGround: true,
    t: 0, // run cycle
  });

  const [score, setScore] = useState(0);

  // Helpers
  // const groundY = () => height - gameRef.current.trackH;
  // const reset = () => {
  //   const g = gameRef.current;
  //   const p = playerRef.current;
  //   g.hurdles = [];
  //   g.score = 0;
  //   g.speed = 300;
  //   g.lastSpawn = 0;
  //   g.trackH = Math.floor(height * 0.35);
  //   p.x = Math.max(120, width * 0.22);
  //   p.r = Math.max(20, Math.min(34, Math.floor(width / 28)));
  //   p.y = groundY() - p.r - 6;
  //   p.vy = 0;
  //   p.onGround = true;
  //   p.t = 0;
  // };
  const groundY = useCallback(() => height - gameRef.current.trackH, [height]);

  const reset = useCallback(() => {
    const g = gameRef.current;
    const p = playerRef.current;
    g.hurdles = [];
    g.score = 0;
    g.speed = 300;
    g.lastSpawn = 0;
    g.trackH = Math.floor(height * 0.35);
    p.x = Math.max(120, width * 0.22);
    p.r = Math.max(20, Math.min(34, Math.floor(width / 28)));
    p.y = groundY() - p.r - 6;
    p.vy = 0;
    p.onGround = true;
    p.t = 0;
  }, [width, height, groundY]);

  const jump = () => {
    const p = playerRef.current;
    if (p.onGround) {
      p.vy = -gameRef.current.jumpVel;
      p.onGround = false;
    }
  };

  // Input
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.repeat) return;
      if (e.code === "Space" || e.code === "ArrowUp" || e.code === "KeyW") {
        e.preventDefault();
        jump();
      }
    };
    const onPointer = () => jump();

    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointer);
    };
  }, []);

  // Main loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let last = performance.now();
    reset();

    const spawnHurdle = () => {
      const gy = groundY();
      const h = 52 + Math.random() * 10;
      gameRef.current.hurdles.push({ x: width + 40, y: gy, h });
    };

    const step = (now: number) => {
      const g = gameRef.current;
      const p = playerRef.current;
      const dt = Math.min(0.033, (now - last) / 1000);
      last = now;

      // --- Update
      g.score += dt * 60;
      setScore(Math.floor(g.score));

      g.speed += g.speedGain * dt;

      // Hurdles spawn
      g.lastSpawn -= dt;
      const minGap = Math.max(0.8, 1.6 - g.speed / 800); // seconds
      if (g.lastSpawn <= 0) {
        spawnHurdle();
        g.lastSpawn = minGap + Math.random() * minGap * 0.9;
      }
      // Move hurdles
      for (let i = g.hurdles.length - 1; i >= 0; i--) {
        const o = g.hurdles[i];
        o.x -= g.speed * dt;
        if (o.x < -140) g.hurdles.splice(i, 1);
      }

      // Physics
      p.vy += g.gravity * dt;
      p.y += p.vy * dt;
      const gy = groundY() - 6;
      if (p.y > gy - p.r) {
        p.y = gy - p.r;
        p.vy = 0;
        p.onGround = true;
      } else {
        p.onGround = false;
      }

      // Collision (circle vs hurdle bar)
      const collides = g.hurdles.some((o) => {
        const rx = o.x - 50; // visual bar from rx..rx+100 at height o.h
        const ry = o.y - o.h;
        const rw = 100;
        const rh = 8;
        const cx = p.x;
        const cy = p.y + 6;
        const cr = p.r * 0.75;
        const testX = Math.max(rx, Math.min(cx, rx + rw));
        const testY = Math.max(ry, Math.min(cy, ry + rh));
        const dx = cx - testX;
        const dy = cy - testY;
        return dx * dx + dy * dy <= cr * cr;
      });
      if (collides) reset();

      // --- Render
      ctx.clearRect(0, 0, width, height);
      // sky
      const grad = ctx.createLinearGradient(0, 0, 0, height);
      grad.addColorStop(0, "#8ec5ff");
      grad.addColorStop(1, "#eaf6ff");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      // track
      const gy2 = groundY();
      ctx.fillStyle = "#2fa24b"; // grass
      ctx.fillRect(0, gy2 - 40, width, 40);
      ctx.fillStyle = "#e63d3d"; // track
      ctx.fillRect(0, gy2, width, height - gy2);
      ctx.setLineDash([14, 10]);
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 3;
      for (let i = 0; i < 4; i++) {
        const y = gy2 + 24 + i * 26;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
      ctx.setLineDash([]);

      // hurdles
      g.hurdles.forEach((o) => {
        ctx.save();
        ctx.translate(o.x, o.y);
        ctx.fillStyle = "#fff";
        ctx.strokeStyle = "#b91c1c";
        ctx.lineWidth = 3;
        ctx.fillRect(-50, -o.h, 100, 8); // bar
        ctx.strokeRect(-50, -o.h, 100, 8);
        ctx.restore();
      });

      // player (Addy)
      const r = p.r;
      // shadow
      ctx.fillStyle = "rgba(0,0,0,.2)";
      ctx.beginPath();
      ctx.ellipse(p.x, gy2 + 6, r * 0.9, 10, 0, 0, Math.PI * 2);
      ctx.fill();

      // torso
      ctx.fillStyle = "#0ea5e9";
      ctx.fillRect(p.x - r * 0.45, p.y - r * 0.2, r * 0.9, r * 0.95);
      // head
      ctx.fillStyle = "#ffe4b5";
      ctx.beginPath();
      ctx.arc(p.x, p.y - r * 0.55, r * 0.55, 0, Math.PI * 2);
      ctx.fill();
      // hair (blonde + small pony bounce)
      const pony = p.onGround ? Math.sin(p.t * 8) * 0.2 : -p.vy / 900;
      ctx.save();
      ctx.translate(p.x - r * 0.1, p.y - r * 0.9);
      ctx.rotate(pony);
      ctx.strokeStyle = "#f7d76c";
      ctx.lineWidth = 8;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(-r * 0.1, -r * 0.6);
      ctx.stroke();
      ctx.restore();

      // run cycle tick (only while grounded)
      p.t += dt;

      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [width, height, groundY, reset]);

  return (
    <div style={{ position: "relative", width, height, userSelect: "none" }}>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{ width, height, display: "block", background: "#8ec5ff" }}
        aria-label="Fast Addy runner canvas"
      />

      {/* Simple overlay UI */}
      <div style={{ position: "absolute", top: 8, left: 8, fontWeight: 700 }}>
        <span style={{ padding: "6px 10px", borderRadius: 999, background: "#fff" }}>
          Score: {score}
        </span>
      </div>

      <button
        onClick={jump}
        aria-label="Jump"
        title="Jump (Space)"
        style={{
          position: "absolute",
          left: "50%",
          bottom: 12,
          transform: "translateX(-50%)",
          padding: "10px 16px",
          borderRadius: 999,
          border: "1px solid #e5e7eb",
          fontWeight: 800,
          background: "#ffffff",
          cursor: "pointer",
        }}
      >
        Jump
      </button>
    </div>
  );
}

// Usage example:
// import FastAddyRunner from "./FastAddyRunner";
// <FastAddyRunner width={800} height={420} />

